import { useQuery } from '@apollo/client';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { CircularProgress } from '@ovComponents/2-component/circularProgress/circularProgress';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { UserContext } from '@providers/userContextProvider';
import { useContext, useMemo, useState } from 'react';
import { FETCH_UPCOMING_TRANSFERS } from '../queries';
import {
  BOLD_SX,
  BORDER_RIGHT,
  SECONDARY_TEXT_SX,
  SECTION_HEADER_SX,
  TRUNCATE_SX,
} from '../styles';
import { formatMoney } from '../utils';

const fmt = (cents: number) =>
  formatMoney(cents, { decimals: Math.abs(cents) >= 100_000_00 ? 0 : 2 });

const relDate = (dateStr: string) => {
  const d = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.ceil((d.getTime() - now.getTime()) / 86_400_000);
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays <= 7) return `${diffDays}d`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const fullDate = (dateStr: string | null) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const urgencyBucket = (
  dateStr: string | null,
): '24h' | '7d' | '30d' | 'later' => {
  if (!dateStr) return '30d';
  const d = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.ceil((d.getTime() - now.getTime()) / 86_400_000);
  if (diffDays <= 1) return '24h';
  if (diffDays <= 7) return '7d';
  if (diffDays <= 30) return '30d';
  return 'later';
};

const FREQ_MAP: Record<string, string> = {
  ONE_TIME: 'One-time',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  SEMI_MONTHLY: 'Bi-monthly',
  WEEKLY: 'Weekly',
  BI_WEEKLY: 'Bi-weekly',
  ANNUALLY: 'Annual',
};

const FREQ_SHORT: Record<string, string> = {
  ONE_TIME: '1x',
  MONTHLY: 'Mo',
  QUARTERLY: 'Qtr',
  SEMI_MONTHLY: '2×Mo',
  WEEKLY: 'Wk',
  BI_WEEKLY: '2Wk',
  ANNUALLY: 'Yr',
};

const STATE_DISPLAY: Record<
  string,
  {
    label: string;
    color: 'warning' | 'positive' | 'neutral' | 'negative' | 'supportOne';
  }
> = {
  INITIATED: { label: 'PENDING', color: 'neutral' },
  REVIEWING: { label: 'REVIEW', color: 'warning' },
  PROCESSING: { label: 'PROCESSING', color: 'warning' },
  ACTIVE: { label: 'ACTIVE', color: 'positive' },
  REQUESTED: { label: 'REQUESTED', color: 'warning' },
  COMPLETED: { label: 'DONE', color: 'positive' },
  FAILED: { label: 'FAILED', color: 'negative' },
  CANCELED: { label: 'CANCELED', color: 'negative' },
};

const TYPE_DISPLAY: Record<string, string> = {
  WITHDRAW: 'Withdrawal',
  withdraw: 'Withdrawal',
  DEPOSIT: 'Deposit',
  deposit: 'Deposit',
  TRANSFER: 'Internal Transfer',
  transfer: 'Internal Transfer',
  EFT: 'EFT',
  eft: 'EFT',
};

type NormalizedFlow = {
  id: string;
  direction: 'INFLOW' | 'OUTFLOW';
  amountCents: number;
  type: string;
  source: string;
  clientName: string;
  accountType: string;
  goalName: string;
  state: string;
  frequency: string;
  scheduledDate: string | null;
  createdAt: string;
  currency: string;
  isRecurring: boolean;
  urgency: '24h' | '7d' | '30d' | 'later';
  withdrawalReason: string | null;
  portfolioMvCents: number;
  portfolioCurrency: string;
  atRisk: boolean;
  subAccountId: string;
  fromSubAccountId: string;
};

type PendingCashFlowsProps = {
  compact?: boolean;
};

const PendingCashFlows = ({ compact }: PendingCashFlowsProps) => {
  const { sys } = useThemeTokens();
  const { activeOrganization } = useContext(UserContext);
  const [urgencyFilter, setUrgencyFilter] = useState<'ALL' | '7d' | '30d'>(
    'ALL',
  );
  const [directionFilter, setDirectionFilter] = useState<
    'ALL' | 'INFLOW' | 'OUTFLOW'
  >('ALL');
  const [recurrenceFilter, setRecurrenceFilter] = useState<
    'ALL' | 'ONE_TIME' | 'RECURRING'
  >('ALL');
  const [atRiskOnly, setAtRiskOnly] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, loading } = useQuery(FETCH_UPCOMING_TRANSFERS, {
    variables: {
      input: {
        filter: { organizationId: activeOrganization?.id },
        pagination: {
          perPage: 200,
          sortField: 'scheduledDate',
          sortDesc: false,
        },
      },
    },
    skip: !activeOrganization?.id,
    fetchPolicy: 'cache-first',
  });

  const flows: NormalizedFlow[] = useMemo(() => {
    const transfers = data?.fetchUpcomingTransfers?.upcomingTransfers || [];
    const mapped = transfers.map((t: any) => {
      const isOutflow = t.type === 'WITHDRAW' || t.type === 'withdraw';
      const acct = t.subAccount?.account || t.fromSubAccount?.account;
      const fullName = [acct?.user?.firstName, acct?.user?.lastName]
        .filter(Boolean)
        .join(' ');
      const mvCents = t.subAccount?.statistics?.marketValueCents || 0;
      const mvCurrency = t.subAccount?.statistics?.currency || '';
      const urg = urgencyBucket(t.scheduledDate);
      return {
        id: t.id,
        direction: isOutflow ? ('OUTFLOW' as const) : ('INFLOW' as const),
        amountCents: t.amountCents || 0,
        type: t.type || '',
        source: t.source || '',
        clientName: fullName || '—',
        accountType: acct?.type || '',
        goalName:
          t.subAccount?.goal?.name || t.fromSubAccount?.goal?.name || '',
        state: t.state || '',
        frequency: t.frequency || 'ONE_TIME',
        scheduledDate: t.scheduledDate || null,
        createdAt: t.createdAt || '',
        currency: t.currency || '',
        isRecurring: !!t.isRecurring,
        urgency: urg,
        withdrawalReason: t.withdrawalReason || null,
        portfolioMvCents: mvCents,
        portfolioCurrency: mvCurrency,
        atRisk: isOutflow && t.amountCents > mvCents * 0.9,
        subAccountId: t.subAccount?.id || '',
        fromSubAccountId: t.fromSubAccount?.id || '',
      };
    });
    mapped.sort((a: NormalizedFlow, b: NormalizedFlow) => {
      if (a.scheduledDate && b.scheduledDate)
        return (
          new Date(a.scheduledDate).getTime() -
          new Date(b.scheduledDate).getTime()
        );
      if (a.scheduledDate && !b.scheduledDate) return -1;
      if (!a.scheduledDate && b.scheduledDate) return 1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    return mapped;
  }, [data]);

  const filtered = useMemo(
    () =>
      flows.filter((f) => {
        const urgencyOk =
          urgencyFilter === 'ALL' ||
          (urgencyFilter === '7d' &&
            (f.urgency === '24h' || f.urgency === '7d')) ||
          (urgencyFilter === '30d' &&
            (f.urgency === '24h' || f.urgency === '7d' || f.urgency === '30d'));
        const dirOk =
          directionFilter === 'ALL' || f.direction === directionFilter;
        const recOk =
          recurrenceFilter === 'ALL' ||
          (recurrenceFilter === 'RECURRING' ? f.isRecurring : !f.isRecurring);
        const riskOk = !atRiskOnly || f.atRisk;
        return urgencyOk && dirOk && recOk && riskOk;
      }),
    [flows, urgencyFilter, directionFilter, recurrenceFilter, atRiskOnly],
  );

  const stats = useMemo(() => {
    const inflows = flows.filter((f) => f.direction === 'INFLOW');
    const outflows = flows.filter((f) => f.direction === 'OUTFLOW');
    const next7d = flows.filter(
      (f) => f.urgency === '24h' || f.urgency === '7d',
    );
    const atRisk = flows.filter((f) => f.atRisk);
    return {
      totalCount: flows.length,
      inflowCount: inflows.length,
      inflowCents: inflows.reduce((s, f) => s + f.amountCents, 0),
      outflowCount: outflows.length,
      outflowCents: outflows.reduce((s, f) => s + f.amountCents, 0),
      next7dCount: next7d.length,
      atRiskCount: atRisk.length,
      atRiskCents: atRisk.reduce((s, f) => s + f.amountCents, 0),
    };
  }, [flows]);

  if (loading)
    return (
      <Box display='flex' alignItems='center' gap={1} p={2}>
        <CircularProgress size={16} />
        <Typography variant='labelSmall' sx={{ ...SECONDARY_TEXT_SX }}>
          Loading cash activity…
        </Typography>
      </Box>
    );
  if (flows.length === 0)
    return (
      <Box p={compact ? 1.5 : 2}>
        <Typography variant='labelSmall' sx={{ ...SECONDARY_TEXT_SX }}>
          No pending cash activity for this organization.
        </Typography>
      </Box>
    );

  const isMetricActive = (label: string) =>
    (label === 'Total' &&
      urgencyFilter === 'ALL' &&
      directionFilter === 'ALL' &&
      recurrenceFilter === 'ALL' &&
      !atRiskOnly) ||
    (label === 'Inflows' && directionFilter === 'INFLOW') ||
    (label === 'Outflows' && directionFilter === 'OUTFLOW') ||
    (label === 'Next 7d' && urgencyFilter === '7d') ||
    (label === 'At Risk' && atRiskOnly);

  const statItems = [
    {
      label: 'Total',
      value: `${stats.totalCount}`,
      onClick: () => {
        setUrgencyFilter('ALL');
        setDirectionFilter('ALL');
        setRecurrenceFilter('ALL');
        setAtRiskOnly(false);
      },
    },
    {
      label: 'Inflows',
      value: fmt(stats.inflowCents),
      sub: `${stats.inflowCount} transfers`,
      color: sys.color.positive,
      onClick: () =>
        setDirectionFilter(directionFilter === 'INFLOW' ? 'ALL' : 'INFLOW'),
    },
    {
      label: 'Outflows',
      value: fmt(stats.outflowCents),
      sub: `${stats.outflowCount} transfers`,
      onClick: () =>
        setDirectionFilter(directionFilter === 'OUTFLOW' ? 'ALL' : 'OUTFLOW'),
    },
    {
      label: 'Next 7d',
      value: `${stats.next7dCount}`,
      color: stats.next7dCount > 0 ? sys.color.warning : undefined,
      onClick: () => setUrgencyFilter(urgencyFilter === '7d' ? 'ALL' : '7d'),
    },
    ...(stats.atRiskCount > 0
      ? [
          {
            label: 'At Risk',
            value: `${stats.atRiskCount}`,
            sub: fmt(stats.atRiskCents),
            color: sys.color.warning,
            onClick: () => {
              setAtRiskOnly(!atRiskOnly);
              if (!atRiskOnly) {
                setDirectionFilter('ALL');
                setUrgencyFilter('ALL');
                setRecurrenceFilter('ALL');
              }
            },
          },
        ]
      : []),
  ];

  const FILTER_BTN_SX = {
    px: 0.75,
    py: 0.15,
    borderRadius: '4px',
    cursor: 'pointer',
    border: '1px solid',
    transition: 'all 0.12s',
    '&:hover': { bgcolor: sys.color.neutralHover },
  };

  return (
    <Box>
      <Box
        display='flex'
        gap={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: sys.color.outlineVariant,
        }}
      >
        {statItems.map((stat: any) => (
          <Box
            key={stat.label}
            flex={1}
            py={0.5}
            px={1}
            onClick={stat.onClick}
            sx={{
              cursor: 'pointer',
              ...BORDER_RIGHT,
              '&:last-child': { borderRight: 'none' },
              '&:hover': { bgcolor: sys.color.neutralHover },
              bgcolor: isMetricActive(stat.label)
                ? sys.color.primaryVariant
                : sys.color.background,
            }}
          >
            <Typography variant='labelSmall' sx={{ ...SECTION_HEADER_SX }}>
              {stat.label}
            </Typography>
            <Typography
              variant='bodySmall'
              sx={{ ...BOLD_SX, color: stat.color || sys.color.onSurface }}
            >
              {stat.value}
            </Typography>
            {stat.sub && (
              <Typography
                variant='labelSmall'
                sx={{ color: stat.color || sys.color.onSurfaceVariant }}
              >
                {stat.sub}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      <Box
        display='flex'
        alignItems='center'
        gap={2}
        px={1.5}
        py={0.5}
        sx={{
          borderBottom: '1px solid',
          borderColor: sys.color.outlineVariant,
          bgcolor: sys.color.background,
        }}
      >
        <Box display='flex' alignItems='center' gap={0.5}>
          <Typography
            variant='labelSmall'
            sx={{ color: sys.color.onDisabled, fontWeight: 600, mr: 0.25 }}
          >
            Direction
          </Typography>
          {(['ALL', 'INFLOW', 'OUTFLOW'] as const).map((val) => {
            const active = directionFilter === val;
            return (
              <Box
                key={val}
                onClick={() => setDirectionFilter(val)}
                sx={{
                  ...FILTER_BTN_SX,
                  bgcolor: active ? sys.color.primaryVariant : 'transparent',
                  borderColor: active
                    ? sys.color.onSurfaceVariant
                    : sys.color.outlineVariant,
                }}
              >
                <Typography
                  variant='labelSmall'
                  sx={{
                    fontWeight: active ? 700 : 400,
                    color: active
                      ? sys.color.onSurface
                      : sys.color.onSurfaceVariant,
                  }}
                >
                  {val === 'ALL' ? 'All' : val === 'INFLOW' ? 'In' : 'Out'}
                </Typography>
              </Box>
            );
          })}
        </Box>
        <Box
          sx={{ width: '1px', height: 16, bgcolor: sys.color.outlineVariant }}
        />
        <Box display='flex' alignItems='center' gap={0.5}>
          <Typography
            variant='labelSmall'
            sx={{ color: sys.color.onDisabled, fontWeight: 600, mr: 0.25 }}
          >
            Type
          </Typography>
          {(['ALL', 'ONE_TIME', 'RECURRING'] as const).map((val) => {
            const active = recurrenceFilter === val;
            return (
              <Box
                key={val}
                onClick={() => setRecurrenceFilter(val)}
                sx={{
                  ...FILTER_BTN_SX,
                  bgcolor: active ? sys.color.primaryVariant : 'transparent',
                  borderColor: active
                    ? sys.color.onSurfaceVariant
                    : sys.color.outlineVariant,
                }}
              >
                <Typography
                  variant='labelSmall'
                  sx={{
                    fontWeight: active ? 700 : 400,
                    color: active
                      ? sys.color.onSurface
                      : sys.color.onSurfaceVariant,
                  }}
                >
                  {val === 'ALL'
                    ? 'All'
                    : val === 'RECURRING'
                      ? 'Recurring'
                      : 'One-time'}
                </Typography>
              </Box>
            );
          })}
        </Box>
        {(directionFilter !== 'ALL' ||
          recurrenceFilter !== 'ALL' ||
          urgencyFilter !== 'ALL' ||
          atRiskOnly) && (
          <>
            <Box
              sx={{
                width: '1px',
                height: 16,
                bgcolor: sys.color.outlineVariant,
              }}
            />
            <Typography
              variant='labelSmall'
              onClick={() => {
                setDirectionFilter('ALL');
                setRecurrenceFilter('ALL');
                setUrgencyFilter('ALL');
                setAtRiskOnly(false);
              }}
              sx={{
                color: sys.color.primary,
                cursor: 'pointer',
                fontWeight: 600,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Clear filters
            </Typography>
          </>
        )}
      </Box>

      <Box
        display='grid'
        gridTemplateColumns='28px 50px 36px 1fr 100px 80px 40px'
        gap={0}
        px={1.5}
        py={0.5}
        sx={{
          borderBottom: '1px solid',
          borderColor: sys.color.outlineVariant,
          bgcolor: sys.color.background,
        }}
      >
        <Box />
        {['When', 'Dir', 'Client / Account', 'Amount', 'Status', 'Freq'].map(
          (h) => (
            <Typography
              key={h}
              variant='labelSmall'
              sx={{ ...SECTION_HEADER_SX }}
            >
              {h}
            </Typography>
          ),
        )}
      </Box>

      <Box sx={{ maxHeight: compact ? 280 : 400, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <Box p={2} textAlign='center'>
            <Typography variant='labelSmall' sx={{ ...SECONDARY_TEXT_SX }}>
              No transfers match current filters
            </Typography>
          </Box>
        ) : (
          filtered.map((f) => (
            <CashFlowRow
              key={f.id}
              flow={f}
              expanded={expandedId === f.id}
              onToggle={() => setExpandedId(expandedId === f.id ? null : f.id)}
              sys={sys as any}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

const CashFlowRow = ({
  flow: f,
  expanded,
  onToggle,
  sys,
}: {
  flow: NormalizedFlow;
  expanded: boolean;
  onToggle: () => void;
  sys: { color: Record<string, string> };
}) => {
  const stateDisplay = STATE_DISPLAY[f.state] || {
    label: f.state,
    color: 'neutral' as const,
  };

  return (
    <Box
      sx={{
        borderBottom: '1px solid',
        borderColor: sys.color.outlineVariant,
        borderLeft: f.atRisk ? '3px solid' : '3px solid transparent',
        borderLeftColor: f.atRisk ? sys.color.warning : 'transparent',
        bgcolor: expanded ? sys.color.primaryVariant : 'transparent',
        '&:hover': { bgcolor: sys.color.neutralHover },
        transition: 'background-color 0.12s',
      }}
    >
      <Box
        display='grid'
        gridTemplateColumns='28px 50px 36px 1fr 100px 80px 40px'
        gap={0}
        px={1.5}
        py={0.4}
        alignItems='center'
        onClick={onToggle}
        sx={{ cursor: 'pointer' }}
      >
        <IconButton onClick={onToggle} sx={{ p: 0 }}>
          {expanded ? (
            <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
          ) : (
            <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
        <Typography
          variant='labelSmall'
          sx={{
            fontWeight: f.urgency === '24h' ? 700 : 400,
            color:
              f.urgency === '24h' ? sys.color.warning : sys.color.onSurface,
          }}
        >
          {f.scheduledDate ? relDate(f.scheduledDate) : 'Pending'}
        </Typography>
        <Badge
          label={f.direction === 'INFLOW' ? 'IN' : 'OUT'}
          color={f.direction === 'INFLOW' ? 'positive' : 'neutral'}
        />
        <Box overflow='hidden'>
          <Typography
            variant='labelSmall'
            sx={{ fontWeight: 600, ...TRUNCATE_SX }}
          >
            {f.clientName}
          </Typography>
          <Typography
            variant='labelSmall'
            sx={{ ...SECONDARY_TEXT_SX, ...TRUNCATE_SX }}
          >
            {[f.accountType, f.goalName].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        <Typography
          variant='labelSmall'
          sx={{
            fontWeight: 600,
            color:
              f.direction === 'INFLOW'
                ? sys.color.positive
                : sys.color.onSurface,
          }}
        >
          {f.direction === 'OUTFLOW' ? '−' : '+'}
          {fmt(f.amountCents)} {f.currency}
        </Typography>
        <Badge label={stateDisplay.label} color={stateDisplay.color} />
        <Typography
          variant='labelSmall'
          sx={{
            color: f.isRecurring
              ? sys.color.onSurface
              : sys.color.onSurfaceVariant,
            fontWeight: f.isRecurring ? 600 : 400,
          }}
        >
          {FREQ_SHORT[f.frequency] || f.frequency?.slice(0, 4) || '—'}
        </Typography>
      </Box>

      {expanded && (
        <Box
          px={2}
          py={1.5}
          mx={1.5}
          mb={1}
          sx={{
            bgcolor: sys.color.surface,
            borderRadius: 1,
            border: '1px solid',
            borderColor: sys.color.outlineVariant,
          }}
        >
          <Box display='grid' gridTemplateColumns='1fr 1fr 1fr' gap={3}>
            <Box>
              <Typography
                variant='labelSmall'
                sx={{ ...SECTION_HEADER_SX, mb: 0.75 }}
              >
                Transfer Details
              </Typography>
              <DetailItem
                sys={sys as any}
                label='Type'
                value={TYPE_DISPLAY[f.type] || f.type}
              />
              <DetailItem
                sys={sys as any}
                label='Direction'
                value={
                  f.direction === 'INFLOW'
                    ? 'Inflow (Deposit)'
                    : 'Outflow (Withdrawal)'
                }
              />
              <DetailItem
                sys={sys as any}
                label='Amount'
                value={`${fmt(f.amountCents)} ${f.currency}`}
                bold
              />
              {f.withdrawalReason && (
                <DetailItem
                  sys={sys as any}
                  label='Reason'
                  value={f.withdrawalReason}
                />
              )}
              {f.source && (
                <DetailItem sys={sys as any} label='Source' value={f.source} />
              )}
            </Box>
            <Box>
              <Typography
                variant='labelSmall'
                sx={{ ...SECTION_HEADER_SX, mb: 0.75 }}
              >
                Schedule
              </Typography>
              <DetailItem
                sys={sys as any}
                label='Scheduled'
                value={fullDate(f.scheduledDate)}
              />
              <DetailItem
                sys={sys as any}
                label='Frequency'
                value={FREQ_MAP[f.frequency] || f.frequency}
              />
              <DetailItem
                sys={sys as any}
                label='Recurring'
                value={f.isRecurring ? 'Yes' : 'No'}
              />
              <DetailItem
                sys={sys as any}
                label='Created'
                value={fullDate(f.createdAt)}
              />
              <DetailItem
                sys={sys as any}
                label='Status'
                value={stateDisplay.label}
              />
            </Box>
            <Box>
              <Typography
                variant='labelSmall'
                sx={{ ...SECTION_HEADER_SX, mb: 0.75 }}
              >
                Account & Risk
              </Typography>
              <DetailItem
                sys={sys as any}
                label='Client'
                value={f.clientName}
                bold
              />
              <DetailItem
                sys={sys as any}
                label='Account'
                value={f.accountType || '—'}
              />
              {f.goalName && (
                <DetailItem sys={sys as any} label='Goal' value={f.goalName} />
              )}
              {f.portfolioMvCents > 0 && (
                <>
                  <DetailItem
                    sys={sys as any}
                    label='Portfolio Value'
                    value={`${fmt(f.portfolioMvCents)} ${f.portfolioCurrency}`}
                  />
                  <DetailItem
                    sys={sys as any}
                    label='% of Portfolio'
                    value={`${((f.amountCents / f.portfolioMvCents) * 100).toFixed(1)}%`}
                    color={
                      f.amountCents / f.portfolioMvCents > 0.5
                        ? sys.color.warning
                        : undefined
                    }
                  />
                </>
              )}
              {f.atRisk && (
                <Box
                  mt={0.75}
                  px={1}
                  py={0.5}
                  sx={{
                    bgcolor: sys.color.neutralHover,
                    borderRadius: 0.5,
                    borderLeft: '3px solid',
                    borderLeftColor: sys.color.warning,
                  }}
                >
                  <Typography
                    variant='labelSmall'
                    sx={{ color: sys.color.warning, fontWeight: 600 }}
                  >
                    Outflow exceeds 90% of portfolio value
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const DetailItem = ({
  label,
  value,
  bold,
  color,
  sys,
}: {
  label: string;
  value: string;
  bold?: boolean;
  color?: string;
  sys: { color: Record<string, string> };
}) => (
  <Box display='flex' justifyContent='space-between' mb={0.25}>
    <Typography variant='labelSmall' sx={{ ...SECONDARY_TEXT_SX }}>
      {label}
    </Typography>
    <Typography
      variant='labelSmall'
      sx={{ ...(bold ? BOLD_SX : {}), color: color ?? sys.color.onSurface }}
    >
      {value}
    </Typography>
  </Box>
);

export default PendingCashFlows;
